
import { CuratedFolderListView } from 'components/CuratedFolderView'
import { useMountEffect } from 'hooks/useMountEffect'
import { CuratedFolder, StatusIdea } from 'interfaces'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, mergeMap } from 'rxjs/operators'
import { getStatusIdeas } from 'services/content/statuses/actions'
import { StoreThunkDispatch } from 'store/state'

interface StatusIdeasCarouselProps {
  items: StatusIdea[]
  page: number
  category: CuratedFolder
  onStatusesFetched: (categoryId: string, items: StatusIdea[], page: number) => void
  onLoadMore: (categoryId: string) => void
}

export function StatusIdeasCarousel(props: StatusIdeasCarouselProps) {
  const { onLoadMore } = props
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [loading, setLoading] = useState(true)
  const fetch$ = useRef<Subject<number>>(undefined)
  const seedRef = useRef(Date.now().toString())
  const pageRef = useRef(props.page)
  const categoryRef = useRef(props.category)

  useMountEffect(() => {
    fetch$.current = new Subject()
    const sub = fetch$.current
      .pipe(
        mergeMap(() => {
          setLoading(true)
          const ids = [categoryRef.current.streams[0].id.toString()]
          // EXPL: fresh random seed for the first page, reused for its subsequent pages
          if (pageRef.current === 0) {
            seedRef.current = Date.now().toString()
          }
          return dispatch(getStatusIdeas(ids, pageRef.current, seedRef.current))
            .pipe(catchError(() => {
              return observableOf({ items: [] })
            }))
        })
      )
      .subscribe((data) => {
        setLoading(false)
        props.onStatusesFetched(categoryRef.current.id, data.items, pageRef.current)
      })

    return () => {
      sub.unsubscribe()
    }
  })

  useEffect(() => {
    categoryRef.current = props.category
    if (props.page === 0 || pageRef.current !== props.page) {
      pageRef.current = props.page
      fetch$.current?.next(props.page)
    }
  }, [props.category, props.page])

  const loadNextPage = useCallback(() => {
    onLoadMore(categoryRef.current.id)
  }, [onLoadMore])

  return (
    <CuratedFolderListView
      stream={categoryRef.current as any}
      items={props.items}
      loading={loading}
      onScrollLimit={loadNextPage}
    />
  )
}
